import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

type Value = string | string[];

interface SingleQueryParam {
  key: string;
  value: Value;
}

export type QueryParams = Record<string, Value>;

interface QueryParamsOptions {
  state?: any;
}

type UrlParam = [key: string, value: string];

type SetQueryParams = (params: QueryParams) => void;

type UseQueryParams = (queryParamsOptions?: QueryParamsOptions) => [QueryParams, SetQueryParams];

const isValueEmpty = (value: Value): boolean => {
  return Array.isArray(value) ? value.length === 0 : value === '';
};

const keyValueTupleToSingleQueryParam = ([key, value]: [key: string, value: Value]): SingleQueryParam => {
  return { key, value };
};

const urlParamValueToValue = (urlParamValue: string): Value => {
  let paramValue: Value = urlParamValue.split(',');

  if (paramValue.length === 1) {
    paramValue = paramValue[0];
  }

  return paramValue;
};

const singleQueryParamToUrlParam = (param: SingleQueryParam): UrlParam => {
  const { key, value: valueOrValues } = param;
  return [key, Array.isArray(valueOrValues) ? valueOrValues.join(',') : valueOrValues];
};

const urlParamsToSearchParams = (urlParams: UrlParam[]): string => {
  return new URLSearchParams(urlParams).toString();
};

const searchParamsToQueryParams = (searchParams: URLSearchParams): QueryParams => {
  return Array.from(searchParams.entries()).reduce<QueryParams>((accumulator, entry) => {
    const [key, value] = entry;
    accumulator[key] = urlParamValueToValue(value);
    return accumulator;
  }, {});
};

const useQueryParams: UseQueryParams = (options?: QueryParamsOptions) => {
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const [params, setParams] = useState<QueryParams>(searchParamsToQueryParams(searchParams));

  const handleParamsChanges = useCallback((): void => {
    const newQueryParams: QueryParams = Array.from(searchParams.entries()).reduce<QueryParams>((accumulator, entry) => {
      const [key, value] = entry;
      accumulator[key] = urlParamValueToValue(value);
      return accumulator;
    }, {});

    setParams(newQueryParams);
  }, [searchParams]);

  useEffect(() => {
    handleParamsChanges();
  }, [handleParamsChanges, location.search]);

  const setQueryParams = useCallback<SetQueryParams>(
    queryParams => {
      const urlParams = Object.entries(queryParams)
        .map(entry => keyValueTupleToSingleQueryParam(entry))
        .filter(({ value }) => !isValueEmpty(value))
        .map(givenParam => singleQueryParamToUrlParam(givenParam));

      const newSearchParams = urlParamsToSearchParams(urlParams);
      navigate({ search: newSearchParams }, { state: options?.state });
    },
    [navigate, options],
  );

  return [params, setQueryParams];
};

export default useQueryParams;
